export interface InvoiceLineItem {
  sno: number;
  description: string;
  qty: string;
  rate: string;
  amount: string;
}

export interface InvoiceData {
  // Company (seller)
  farmName: string;
  productionAddress?: string;
  registeredAddress?: string;
  stateName?: string;
  stateCode?: string;
  msmeNumber?: string;
  panNumber?: string;
  contactPhone?: string;
  email?: string;
  farmGst?: string;
  logoUrl?: string;

  // Invoice meta
  invoiceNumber: string;
  invoiceDate: string;

  // Buyer (bill to)
  buyerName?: string;
  buyerAddress?: string;
  buyerGstin?: string;
  buyerStateName?: string;
  buyerStateCode?: string;

  // Line items
  lineItems: InvoiceLineItem[];
  totalAmount: string;
  amountInWords?: string;

  // Bank details
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankBranchIfsc?: string;

  // Footer
  declarationText?: string;
  jurisdictionText?: string;
  signatureUrl?: string;
}

function esc(str: string): string {
  const el = document.createElement("span");
  el.textContent = str;
  return el.innerHTML;
}

function buildInvoiceBody(data: InvoiceData, copyLabel: string): string {
  const rows = data.lineItems
    .map(
      (item) => `
        <tr>
          <td class="bc" style="text-align:right;width:50px">${item.sno}</td>
          <td class="bc">${esc(item.description)}</td>
          <td class="bc" style="text-align:right">${esc(item.qty)}</td>
          <td class="bc" style="text-align:right">\u20B9${esc(item.rate)}</td>
          <td class="bc" style="text-align:right">\u20B9${esc(item.amount)}</td>
        </tr>`
    )
    .join("");

  const bankSection =
    data.bankAccountName || data.bankName
      ? `<b>Company's Bank Details:</b><br>
         ${data.bankAccountName ? `A/c Holder's Name : ${esc(data.bankAccountName)}<br>` : ""}
         ${data.bankName ? `Bank Name : ${esc(data.bankName)}<br>` : ""}
         ${data.bankAccountNumber ? `A/c No. : ${esc(data.bankAccountNumber)}<br>` : ""}
         ${data.bankBranchIfsc ? `Branch & IFS Code : ${esc(data.bankBranchIfsc)}` : ""}`
      : "";

  return `<div class="inv">
  <div class="hdr">INVOICE <span style="float:right;font-size:11px;font-weight:400;letter-spacing:0">${esc(copyLabel)}</span></div>
  <table>
    <tr>
      <td class="bc" rowspan="3" style="width:55%;vertical-align:top;padding:8px">
        <div class="company-info">
          ${data.logoUrl ? `<img src="${esc(data.logoUrl)}" style="height:40px;float:left;margin-right:8px" alt="Logo">` : ""}
          <b>${esc(data.farmName)}</b><br>
          ${data.productionAddress ? `Production Unit: ${esc(data.productionAddress)}<br>` : ""}
          ${data.registeredAddress ? `Reg. Address: ${esc(data.registeredAddress)}<br>` : ""}
          ${data.stateName ? `State Name: ${esc(data.stateName)}${data.stateCode ? `, Code : ${esc(data.stateCode)}` : ""}<br>` : ""}
          ${data.msmeNumber ? `MSME NO. ${esc(data.msmeNumber)}<br>` : ""}
          ${data.panNumber ? `PAN : ${esc(data.panNumber)}<br>` : ""}
          ${data.contactPhone ? `Contact: ${esc(data.contactPhone)}<br>` : ""}
          ${data.email ? `E-Mail: ${esc(data.email)}` : ""}
        </div>
      </td>
      <td class="bc" style="width:27%">
        <span class="meta-label">Invoice No.</span><br>
        <b>${esc(data.invoiceNumber)}</b>
      </td>
      <td class="bc" style="width:18%">
        <span class="meta-label">Date</span><br>
        <b>${esc(data.invoiceDate)}</b>
      </td>
    </tr>
    <tr>
      <td class="bc" colspan="2" style="vertical-align:top">
        <span class="meta-label">Bill to:</span><br>
        ${data.buyerName ? `<b>${esc(data.buyerName)}</b><br>` : ""}
        ${data.buyerAddress ? `${esc(data.buyerAddress)}<br>` : ""}
        ${data.buyerGstin ? `GSTIN/UIN : ${esc(data.buyerGstin)}<br>` : ""}
        ${data.buyerStateName ? `State: ${esc(data.buyerStateName)}${data.buyerStateCode ? `, Code: ${esc(data.buyerStateCode)}` : ""}` : ""}
      </td>
    </tr>
    <tr>
      <td class="bc" colspan="2">
        <span class="meta-label">Ship to:</span>
        <div style="text-align:right;font-size:11px">Not Applicable</div>
      </td>
    </tr>
  </table>
  <table>
    <tr style="background:#f5f5f5">
      <th class="bc" style="text-align:center;width:50px">S.No.</th>
      <th class="bc" style="text-align:center">Item</th>
      <th class="bc" style="text-align:center;width:100px">Quantity</th>
      <th class="bc" style="text-align:center;width:90px">Rate/Unit</th>
      <th class="bc" style="text-align:center;width:110px">Amount</th>
    </tr>
    ${rows}
    <tr>
      <td class="bc" colspan="4" style="text-align:right;font-weight:700;padding-right:10px">Total</td>
      <td class="bc total-cell" style="text-align:right">\u20B9${esc(data.totalAmount)}</td>
    </tr>
  </table>
  <table>
    <tr>
      <td class="bc" style="width:45%;vertical-align:top">
        <b>Amount in Words:</b><br>
        <div style="margin-top:2px;font-style:italic">${data.amountInWords ? esc(data.amountInWords) : ""}</div>
      </td>
      <td class="bc" style="width:55%;vertical-align:top;font-size:11px;line-height:1.5">
        ${bankSection}
      </td>
    </tr>
  </table>
  <table>
    <tr>
      <td class="bc" style="width:50%;vertical-align:top;padding:6px;font-size:10px">
        ${data.declarationText ? `<b>Declaration</b><br><span style="text-decoration:underline">${esc(data.declarationText)}</span>` : ""}
      </td>
      <td class="bc" style="width:50%;text-align:right;vertical-align:top;padding:6px">
        <div style="font-size:11px;font-weight:600">for ${esc(data.farmName)}</div>
        ${data.signatureUrl ? `<img src="${esc(data.signatureUrl)}" style="height:35px;margin:4px 0" alt="Signature">` : '<div style="height:35px"></div>'}
        <div style="font-size:10px;color:#555">Authorised Signatory</div>
      </td>
    </tr>
  </table>
  ${data.jurisdictionText ? `<div style="text-align:center;padding:4px;font-size:10px;font-weight:600;border-top:1px solid #999;background:#f9f9f9">${esc(data.jurisdictionText)}</div>` : ""}
</div>`;
}

function buildInvoiceHTML(data: InvoiceData): string {
  const original = buildInvoiceBody(data, "Original Copy");
  const duplicate = buildInvoiceBody(data, "Duplicate Copy");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Invoice ${esc(data.invoiceNumber)}</title>
  <style>
    @page { margin: 8mm; size: A4; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; color: #222; font-size: 11px; line-height: 1.3; }
    .inv { width: 100%; max-width: 780px; margin: 0 auto; border: 2px solid #333; }
    .bc { border: 1px solid #999; padding: 4px 6px; }
    .hdr { background: #14302a; color: #fff; text-align: center; font-size: 14px; font-weight: 700; padding: 6px; letter-spacing: 1px; }
    table { width: 100%; border-collapse: collapse; }
    .company-info { font-size: 10.5px; line-height: 1.4; }
    .company-info b { font-size: 12px; }
    .meta-label { font-size: 10px; color: #555; }
    .total-cell { font-weight: 700; font-size: 12px; }
    .tear-line { border-top: 2px dashed #999; margin: 8px 0; text-align: center; position: relative; }
    .tear-line span { background: #fff; padding: 0 10px; position: relative; top: -9px; font-size: 10px; color: #999; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .inv { border: 2px solid #333; }
      .hdr { background: #14302a !important; color: #fff !important; }
    }
  </style>
</head>
<body>
${original}
<div class="tear-line"><span>&#9986; Cut Here</span></div>
${duplicate}
</body>
</html>`;
}

/**
 * Opens a print window immediately (call from click handler to avoid popup blockers).
 */
export function openPrintWindow(): Window | null {
  return window.open("", "_blank", "width=800,height=600");
}

export function writeAndPrint(printWindow: Window, data: InvoiceData): void {
  const html = buildInvoiceHTML(data);
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();

    const cleanup = () => printWindow.close();
    if ("onafterprint" in printWindow) {
      printWindow.onafterprint = cleanup;
    }
    setTimeout(cleanup, 3000);
  }, 200);
}
